import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ShieldCheck, ShieldAlert, RefreshCw, FileSignature } from "lucide-react";

const ACTION_LABEL = {
  post: "Boeking", correct: "Correctie", create: "Aanmaak", update: "Wijziging", upload: "Upload",
};

export default function AuditLog() {
  const [records, setRecords] = useState([]);
  const [verify, setVerify] = useState(null);

  const load = () => {
    api.get("/audit").then((r) => setRecords(r.data));
    api.get("/audit/verify").then((r) => setVerify(r.data));
  };
  useEffect(() => { load(); }, []);

  const exportSigned = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/audit/export`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("export mislukt");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `audit-overzicht-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      toast.success("Ondertekend audit-overzicht (PDF) gedownload");
    } catch (e) {
      toast.error("Kon audit-overzicht niet exporteren");
    }
  };

  return (
    <div className="space-y-6" data-testid="audit-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight flex items-center gap-2">
            <ShieldCheck className="h-7 w-7 text-primary" /> Audit trail
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Cryptografisch verzegelde, append-only geschiedenis (SHA-256 hashketen)</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={load} data-testid="refresh-audit"><RefreshCw className="h-4 w-4 mr-2" /> Vernieuwen</Button>
          <Button onClick={exportSigned} data-testid="export-audit"><FileSignature className="h-4 w-4 mr-2" /> Ondertekend overzicht</Button>
        </div>
      </div>

      {verify && (
        <Card className={`shadow-sm border-2 ${verify.valid ? "border-emerald-300" : "border-destructive"}`} data-testid="audit-verify">
          <CardContent className="p-5 flex items-center gap-4">
            {verify.valid ? <ShieldCheck className="h-10 w-10 text-emerald-600" /> : <ShieldAlert className="h-10 w-10 text-destructive" />}
            <div className="flex-1">
              <div className="font-heading text-lg font-semibold">
                {verify.valid ? "Integriteit geverifieerd" : `Integriteit geschonden bij sequentie ${verify.broken_at_seq}`}
              </div>
              <div className="text-sm text-muted-foreground">{verify.records} gebeurtenissen · hashketen {verify.valid ? "intact en onveranderd" : "beschadigd"}</div>
            </div>
            {verify.head_hash && <div className="font-mono text-xs text-muted-foreground hidden md:block">HEAD: {verify.head_hash.slice(0, 16)}…</div>}
          </CardContent>
        </Card>
      )}

      <Card className="shadow-sm">
        <CardHeader><CardTitle className="font-heading text-lg">Gebeurtenissen</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow><TableHead>#</TableHead><TableHead>Tijdstip</TableHead><TableHead>Actie</TableHead><TableHead>Type</TableHead><TableHead>Gebruiker</TableHead><TableHead>Hash</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {records.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">Nog geen gebeurtenissen.</TableCell></TableRow>}
              {records.map((r) => (
                <TableRow key={r.seq} data-testid={`audit-row-${r.seq}`}>
                  <TableCell className="font-mono text-sm">{r.seq}</TableCell>
                  <TableCell className="text-sm">{new Date(r.timestamp).toLocaleString("nl-BE")}</TableCell>
                  <TableCell><Badge variant="outline">{ACTION_LABEL[r.action] || r.action}</Badge></TableCell>
                  <TableCell className="text-sm">{r.entity_type}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{r.user_email}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{r.hash.slice(0, 12)}…</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
